import EzController from 'controllers/ez-base-controller';
import MediaMatcher from 'lib/media-matcher';
import { getContentType, getWindowWidth } from 'lib/helpers';
import { getResourceType } from 'lib/resource-show-helpers';
import { sendTrackingEvent } from 'components/analytics/conversions/tracking';
import { EDITOR_CLICK, ZOOM_CLICK } from 'components/analytics/conversions/constants/actions';

const mediaMatcher = new MediaMatcher();

export default class ResourceShowPreviewController extends EzController {
  static targets = [
    'previewImage',
    'previewWrap',
    'previewInnerWrap',
    'previewOuterWrap',
    'previewVideoWrap',
  ];

  connect() {
    if (this.previewContentType === 'video') {
      this.flagLoaded();
      this.setVideoAspectRatio();

      return;
    }

    this.img = this.previewImageTarget;

    this.maxPreviewHeight = 560;

    window.addEventListener('PreviewImageChanged', this.resizePreview);

    window.addEventListener('resize', this.resizePreview);

    this.upgrade2x();

    this.previewOuterWrapTarget.addEventListener('contextmenu', this.preventRightClick);

    this.setupForZoom();

    if (this.canZoom) {
      mediaMatcher.evaluateAll();
    }

    this.resizePreview();

    mediaMatcher.addListeners();

    this.flagLoaded();
  }

  disconnect() {
    if (this.previewContentType === 'video') return;

    this.previewOuterWrapTarget.removeEventListener('contextmenu', this.preventRightClick);

    window.removeEventListener('resize', this.resizePreview);

    mediaMatcher.removeListeners();
    mediaMatcher.clearQueries();

    if (this.canZoom) {
      this.removeZoomListeners(this.previewOuterWrapTarget);
      this.removeZoomListeners(this.img);
    }
  }

  setVideoAspectRatio() {
    let pollIndex = 0;

    const plyrWrapperCheck = setInterval(() => {
      if (this.previewVideoWrapTarget.querySelector('.plyr__video-wrapper')) {
        const videoAspectRatio = parseFloat(this.previewVideoWrapTarget.getAttribute('data-aspect-ratio'));
        const plyrWrapper = this.previewVideoWrapTarget.querySelector('.plyr__video-wrapper');

        plyrWrapper.style.paddingTop = `${videoAspectRatio * 100}%`;

        clearInterval(plyrWrapperCheck);
      } else {
        pollIndex += 1;

        if (pollIndex === 100) {
          // don't keep polling if target element is never found
          clearInterval(plyrWrapperCheck);
        }
      }
    }, 100);
  }

  get canZoom() {
    if (getWindowWidth() <= 1100) return false;

    return this.previewOuterWrapTarget.hasAttribute('data-zoomable');
  }

  get previewContentType() {
    return this.previewOuterWrapTarget.getAttribute('data-resource-show-preview-content-type');
  }

  preventRightClick(event) {
    event.preventDefault();
  }

  setupForZoom = () => {
    mediaMatcher.addMediaQuery('(min-width: 1101px)',
      () => { if (this.canZoom) this.addZoom(); },
      () => { this.removeZoom(); });
  }

  addZoom() {
    this.zoomScale = this.previewOuterWrapTarget.getAttribute('data-zoom-scale');

    this.previewOuterWrapTarget.classList.add('is-zoomable');

    this.addZoomListeners(this.img);
  }

  removeZoom() {
    this.zoomOut();

    this.previewOuterWrapTarget.classList.remove('is-zoomable');

    this.removeZoomListeners(this.previewOuterWrapTarget);
    this.removeZoomListeners(this.img);
  }

  addZoomListeners(el) {
    el.addEventListener('click', this.toggleZoom);
    el.addEventListener('touchend', this.toggleZoom);
  }

  removeZoomListeners(el) {
    el.removeEventListener('click', this.toggleZoom);
    el.removeEventListener('touchend', this.toggleZoom);
  }

  toggleZoom = (event) => {
    event.stopPropagation();

    // TODO: Maybe re-enable touch behaviors later?
    // Check history of ES-2224 branch for relevant code
    if (event.type === 'touchend') {
      event.preventDefault();
      return;
    }

    if (!this.canZoom) return;

    if (this.zoomed) {
      this.zoomOut();

      this.removeZoomListeners(this.previewOuterWrapTarget);
      this.addZoomListeners(this.img);
    } else {
      this.zoomIn(event);

      this.removeZoomListeners(this.img);
      this.addZoomListeners(this.previewOuterWrapTarget);
    }
  }

  switchToZoomImg() {
    this.img.setAttribute('src', this.img.getAttribute('data-zoom-src'));
    this.img.removeAttribute('srcset');
    this.img.removeAttribute('sizes');

    this.zoomedOnce = true;
  }

  zoomIn = (event) => {
    if (!this.zoomedOnce) this.switchToZoomImg();

    this.previewOuterWrapTarget.classList.add('is-zooming');

    setTimeout(() => {
      this.previewOuterWrapTarget.classList.add('is-zoomed');
      this.previewOuterWrapTarget.classList.remove('is-zooming');
    }, 300);

    this.getPointerPos(event);
    this.updateZoomPosition();

    this.zoomed = true;

    this.previewInnerWrapTarget.addEventListener('mousemove', this.zoomMove);

    sendTrackingEvent({
      element: event.currentTarget,
      data: {
        action: ZOOM_CLICK,
        label: getResourceType(),
        content_type: `Content-${getContentType()}`,
      },
    });
  }

  zoomOut() {
    if (this.previewOuterWrapTarget.classList.contains('is-zoomed')) {
      this.img.style.transform = '';

      this.previewOuterWrapTarget.classList.remove('is-zoomed');

      this.zoomed = false;

      this.previewInnerWrapTarget.removeEventListener('mousemove', this.zoomMove);
    }
  }

  zoomMove = (event) => {
    this.getPointerPos(event);
    this.updateZoomPosition();
  }

  updateZoomPosition() {
    const imgWidth = this.img.width;
    const imgHeight = this.img.height;

    const imgCenterX = imgWidth / 2;
    const imgCenterY = imgHeight / 2;

    const pointToCenterX = ((imgCenterX - this.pointerPositionX) / imgWidth) * 100;
    const pointToCenterY = ((imgCenterY - this.pointerPositionY) / imgHeight) * 100;

    this.setTranslate(pointToCenterX, pointToCenterY, this.img);
  }

  getPointerPos(event) {
    this.pointerPositionX = event.clientX || this.pointerPositionX;
    this.pointerPositionY = event.clientY || this.pointerPositionY;

    const imgOffset = this.previewInnerWrapTarget.getBoundingClientRect();

    this.pointerPositionX -= imgOffset.left;
    this.pointerPositionY -= imgOffset.top;
  }

  setTranslate = (xPos, yPos, el) => {
    el.style.transform = `scale(${this.zoomScale}) translate3d(${xPos}%, ${yPos}%, 0px)`;
  }

  upgrade2x() {
    if (window.devicePixelRatio !== 1) return;

    if (this.previewImageTarget.width > Math.floor(this.previewOuterWrapTarget.dataset.originalWidth / 2)
        || this.previewImageTarget.height > Math.floor(this.previewOuterWrapTarget.dataset.originalHeight / 2)) {
      const imageSrcSet = this.previewImageTarget.getAttribute('srcset');
      const firstSpace = imageSrcSet.indexOf(' ');
      const twoXUrl = imageSrcSet.substring(0, firstSpace);
      this.previewImageTarget.setAttribute('srcset', `${twoXUrl} 1x`);
    }
  }

  resizePreview = () => {
    const maxHeightPromise = () => new Promise((resolve) => this.setPreviewMaxHeight(resolve));

    maxHeightPromise().then(() => this.setPreviewWidth());
  }

  setPreviewWidth() {
    this.previewInnerWrapTarget.style.width = '100%';

    if (getWindowWidth() <= 600) return;

    const previewStyle = window.getComputedStyle(this.previewOuterWrapTarget);
    const vertPadding = parseInt(previewStyle.paddingTop, 10);
    const horizPadding = parseInt(previewStyle.paddingLeft, 10) + parseInt(previewStyle.paddingRight, 10);

    this.previewWidth = this.img.width;
    this.previewHeight = this.img.height;

    const containerWidth = this.previewOuterWrapTarget.clientWidth - horizPadding;
    const containerHeight = this.previewOuterWrapTarget.clientHeight - vertPadding;

    const newWidth = (containerHeight * this.previewWidth) / this.previewHeight;
    const newWidthPerc = (newWidth / containerWidth) * 100;

    this.previewInnerWrapTarget.style.width = `${newWidthPerc}%`;
  }

  setPreviewMaxHeight(resolve) {
    if (getWindowWidth() > 1101) {
      const previewStyle = window.getComputedStyle(this.previewOuterWrapTarget);

      if (this.img.height >= this.maxPreviewHeight) {
        this.previewOuterWrapTarget.style.height = `${this.maxPreviewHeight}px`;
      } else if (parseInt(previewStyle.maxHeight, 10) < this.maxPreviewHeight) {
        this.previewOuterWrapTarget.style.height = null;
      }
    } else {
      this.previewOuterWrapTarget.style.height = null;
    }

    resolve();
  }

  sendEditorButtonEvent(event) {
    event.stopPropagation();

    sendTrackingEvent({
      element: event.currentTarget,
      data: {
        action: EDITOR_CLICK,
        label: getResourceType(),
        content_type: `Content-${getContentType()}`,
      },
    });
  }
}
